import { useEffect, useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { Card } from "@project/components/ui/card";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Textarea } from "@project/components/ui/textarea";
import { Label } from "@project/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Loader2,
  Sparkles,
  Copy,
  BarChart3,
  MessageSquare,
  Mail,
  Newspaper,
  HandHeart,
  UserRound,
  Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@project/lib/data-client";
import {
  generateAdminInsights,
  summariseFeedback,
  draftInquiryReply,
  generatePostCopy,
  draftDonorReactivation,
  generateResidentBio,
  generateThankYou,
} from "@project/lib/ai.functions";

type Opt = { id: string; label: string };

function ToolCard({
  icon,
  title,
  desc,
  accent,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  accent?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="border-gold/30 to-card/60 relative overflow-hidden bg-gradient-to-br from-card p-5 shadow-sm transition hover:shadow-md">
      <div
        className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent ?? "from-gold to-primary"}`}
      />
      <div className="mb-3 flex items-start gap-3">
        <div className="bg-gold/15 ring-gold/30 grid h-10 w-10 place-items-center rounded-lg text-gold ring-1">
          {icon}
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold leading-tight">{title}</h3>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
      </div>
      {children}
    </Card>
  );
}

function Output({
  text,
  busy,
  err,
}: {
  text: string;
  busy: boolean;
  err: string | null;
}) {
  if (!busy && !text && !err) {
    return null;
  }
  return (
    <div className="bg-muted/30 mt-3 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-md border border-border p-3 text-sm">
      {busy && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Thinking…
        </div>
      )}
      {!busy && err && <div className="text-destructive">{err}</div>}
      {!busy && !err && text && (
        <>
          <div>{text}</div>
          <div className="mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(text);
                toast.success("Copied");
              }}
            >
              <Copy className="mr-1 h-3.5 w-3.5" /> Copy
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function useAi<T extends (...a: any[]) => Promise<{ text: string }>>(fn: T) {
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const run = async (...args: Parameters<T>) => {
    setBusy(true);
    setErr(null);
    setText("");
    try {
      const { text } = await fn(...args);
      setText(text);
    } catch (e: any) {
      setErr(e?.message ?? "Failed");
    } finally {
      setBusy(false);
    }
  };
  return { busy, text, err, run };
}

export default function Page() {
  // Reference data
  const [inquiries, setInquiries] = useState<Opt[]>([]);
  const [donors, setDonors] = useState<Opt[]>([]);
  const [residents, setResidents] = useState<Opt[]>([]);
  const [donations, setDonations] = useState<Opt[]>([]);

  useEffect(() => {
    (async () => {
      const [i, d, r, dn] = await Promise.all([
        supabase
          .from("contact_inquiries")
          .select("id, full_name, subject")
          .order("created_at", { ascending: false })
          .limit(50),
        supabase
          .from("donors")
          .select("id, full_name, city")
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("residents")
          .select("id, full_name")
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("donations")
          .select("id, amount, donation_date, donor_id")
          .order("donation_date", { ascending: false })
          .limit(50),
      ]);
      setInquiries(
        (i.data ?? []).map((x: any) => ({
          id: x.id,
          label: `${x.full_name} — ${x.subject ?? "no subject"}`,
        }))
      );
      setDonors(
        (d.data ?? []).map((x: any) => ({
          id: x.id,
          label: `${x.full_name}${x.city ? " · " + x.city : ""}`,
        }))
      );
      setResidents(
        (r.data ?? []).map((x: any) => ({ id: x.id, label: x.full_name }))
      );
      setDonations(
        (dn.data ?? []).map((x: any) => ({
          id: x.id,
          label: `₹${Number(x.amount).toLocaleString("en-IN")} · ${x.donation_date ?? ""}`,
        }))
      );
    })();
  }, []);

  // Hooks per tool
  const insights = useAi(async () => generateAdminInsights());
  const feedback = useAi(async () => summariseFeedback());

  const [invId, setInvId] = useState("");
  const [invTone, setInvTone] = useState<"warm" | "formal" | "apologetic">(
    "warm"
  );
  const inv = useAi(async (id: string, tone: any) =>
    draftInquiryReply({ data: { inquiryId: id, tone } })
  );

  const [pTitle, setPTitle] = useState("");
  const [pCat, setPCat] = useState("");
  const [pNotes, setPNotes] = useState("");
  const post = useAi(async (title: string, category: string, notes: string) =>
    generatePostCopy({
      data: {
        title,
        category: category || undefined,
        notes: notes || undefined,
      },
    })
  );

  const [dnrId, setDnrId] = useState("");
  const [channel, setChannel] = useState<"email" | "whatsapp" | "sms">("email");
  const dnr = useAi(async (id: string, ch: any) =>
    draftDonorReactivation({ data: { donorId: id, channel: ch } })
  );

  const [resId, setResId] = useState("");
  const res = useAi(async (id: string) =>
    generateResidentBio({
      data: { residentId: id, audience: "website", words: 150 },
    })
  );

  const [tyId, setTyId] = useState("");
  const [tyLang, setTyLang] = useState<"english" | "hindi" | "gujarati">(
    "english"
  );
  const [tyTone, setTyTone] = useState<"warm" | "formal" | "spiritual">("warm");
  const ty = useAi(async (id: string, lang: any, tone: any) =>
    generateThankYou({ data: { donationId: id, language: lang, tone } })
  );

  return (
    <AdminShell
      title="AI Studio"
      subtitle="Lovable AI — drafts, summaries and insights for the trust"
    >
      {/* Hero */}
      <div className="border-gold/30 mb-6 overflow-hidden rounded-2xl border bg-gradient-to-br from-primary via-primary to-emerald-700 p-6 text-primary-foreground shadow-lg">
        <div className="flex items-center gap-3">
          <div className="bg-gold/20 ring-gold/40 grid h-12 w-12 place-items-center rounded-xl ring-1">
            <Sparkles className="h-6 w-6 text-gold" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">AI Studio</h2>
            <p className="text-primary-foreground/80 text-sm">
              Seven powered helpers using live trust data. No setup needed.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* 1. Executive insights */}
        <ToolCard
          icon={<BarChart3 className="h-5 w-5" />}
          title="Executive Insights"
          desc="180-day trend analysis & priorities"
        >
          <Button
            onClick={() => insights.run()}
            disabled={insights.busy}
            className="w-full"
          >
            <Sparkles className="mr-2 h-4 w-4" />{" "}
            {insights.text ? "Regenerate" : "Generate briefing"}
          </Button>
          <Output {...insights} />
        </ToolCard>

        {/* 2. Feedback */}
        <ToolCard
          icon={<MessageSquare className="h-5 w-5" />}
          title="Feedback Sentiment"
          desc="Themes & action items across recent feedback"
          accent="from-emerald-500 to-primary"
        >
          <Button
            onClick={() => feedback.run()}
            disabled={feedback.busy}
            className="w-full"
          >
            <Sparkles className="mr-2 h-4 w-4" />{" "}
            {feedback.text ? "Regenerate" : "Summarise feedback"}
          </Button>
          <Output {...feedback} />
        </ToolCard>

        {/* 3. Inquiry reply */}
        <ToolCard
          icon={<Mail className="h-5 w-5" />}
          title="Inquiry Reply Drafter"
          desc="Personalised reply to a contact inquiry"
          accent="from-blue-500 to-primary"
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <Select value={invId} onValueChange={setInvId}>
              <SelectTrigger>
                <SelectValue placeholder="Pick inquiry" />
              </SelectTrigger>
              <SelectContent>
                {inquiries.map(o => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={invTone} onValueChange={v => setInvTone(v as any)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="warm">Warm</SelectItem>
                <SelectItem value="formal">Formal</SelectItem>
                <SelectItem value="apologetic">Apologetic</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => invId && inv.run(invId, invTone)}
            disabled={!invId || inv.busy}
            className="mt-2 w-full"
          >
            <Sparkles className="mr-2 h-4 w-4" /> Draft reply
          </Button>
          <Output {...inv} />
        </ToolCard>

        {/* 4. Post copy */}
        <ToolCard
          icon={<Newspaper className="h-5 w-5" />}
          title="Post / News Copy"
          desc="Short + full description, ready to publish"
          accent="from-violet-500 to-primary"
        >
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={pTitle}
                onChange={e => setPTitle(e.target.value)}
                placeholder="e.g. Annual Sankranti Celebration"
              />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Category</Label>
                <Input
                  value={pCat}
                  onChange={e => setPCat(e.target.value)}
                  placeholder="Event / Update"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                value={pNotes}
                onChange={e => setPNotes(e.target.value)}
                rows={2}
                placeholder="Key facts, names, outcomes…"
              />
            </div>
          </div>
          <Button
            onClick={() => pTitle && post.run(pTitle, pCat, pNotes)}
            disabled={!pTitle || post.busy}
            className="mt-2 w-full"
          >
            <Sparkles className="mr-2 h-4 w-4" /> Draft post
          </Button>
          <Output {...post} />
        </ToolCard>

        {/* 5. Donor reactivation */}
        <ToolCard
          icon={<HandHeart className="h-5 w-5" />}
          title="Donor Re-engagement"
          desc="Win-back outreach for lapsed donors"
          accent="from-rose-500 to-primary"
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <Select value={dnrId} onValueChange={setDnrId}>
              <SelectTrigger>
                <SelectValue placeholder="Pick donor" />
              </SelectTrigger>
              <SelectContent>
                {donors.map(o => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={channel} onValueChange={v => setChannel(v as any)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => dnrId && dnr.run(dnrId, channel)}
            disabled={!dnrId || dnr.busy}
            className="mt-2 w-full"
          >
            <Sparkles className="mr-2 h-4 w-4" /> Draft message
          </Button>
          <Output {...dnr} />
        </ToolCard>

        {/* 6. Resident bio */}
        <ToolCard
          icon={<UserRound className="h-5 w-5" />}
          title="Resident Bio"
          desc="Dignified narrative bio from structured fields"
          accent="from-amber-500 to-primary"
        >
          <Select value={resId} onValueChange={setResId}>
            <SelectTrigger>
              <SelectValue placeholder="Pick resident" />
            </SelectTrigger>
            <SelectContent>
              {residents.map(o => (
                <SelectItem key={o.id} value={o.id}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => resId && res.run(resId)}
            disabled={!resId || res.busy}
            className="mt-2 w-full"
          >
            <Sparkles className="mr-2 h-4 w-4" /> Generate bio
          </Button>
          <Output {...res} />
        </ToolCard>

        {/* 7. Thank-you */}
        <ToolCard
          icon={<Receipt className="h-5 w-5" />}
          title="Thank-you Note"
          desc="Personalised donation thank-you in EN/HI/GU"
          accent="from-teal-500 to-primary"
        >
          <div className="grid gap-2 sm:grid-cols-3">
            <Select value={tyId} onValueChange={setTyId}>
              <SelectTrigger>
                <SelectValue placeholder="Donation" />
              </SelectTrigger>
              <SelectContent>
                {donations.map(o => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={tyLang} onValueChange={v => setTyLang(v as any)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="hindi">Hindi</SelectItem>
                <SelectItem value="gujarati">Gujarati</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tyTone} onValueChange={v => setTyTone(v as any)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="warm">Warm</SelectItem>
                <SelectItem value="formal">Formal</SelectItem>
                <SelectItem value="spiritual">Spiritual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => tyId && ty.run(tyId, tyLang, tyTone)}
            disabled={!tyId || ty.busy}
            className="mt-2 w-full"
          >
            <Sparkles className="mr-2 h-4 w-4" /> Draft thank-you
          </Button>
          <Output {...ty} />
        </ToolCard>
      </div>
    </AdminShell>
  );
}
